/**
 * What a person is pointing at, inside a document they are holding
 * (RFC 0297 §4e).
 *
 * LIVE, NOT FOLDED. A selection says what somebody is pointing at NOW. It
 * changes on every click, and one log entry per click is RFC 0293's keystroke
 * problem with worse economics, so it lives in window state and is written to
 * the log only when a message uses it.
 *
 * STICKY, and this is the decision the RFC left open. `selectionchange` fires
 * when you click anywhere else, including into the composer, so a selection
 * that cleared on collapse would vanish at the exact moment you went to type
 * about it. The last passage stays until another is chosen or it is dismissed.
 * The cost is honest: a selection can be staler than the highlight on screen,
 * which is why the chip is dismissable and says what it is holding.
 *
 * WINDOW STATE, NOT PANE STATE. The selection is made in a document pane and
 * read by the bar above a conversation, which is a different pane. The space is
 * what joins them, exactly as it joins an open document to a session (§4b).
 */

import { createContext, useContext } from 'react'

/** How much of a passage becomes the label. Long enough to recognise a
 *  sentence, short enough that it cannot push the composer off the screen.
 *  Matches the bridge's own cap on what an app view may report. */
export const SELECTION_LABEL_MAX = 200

export interface Selection {
  /** The document the selection is inside. */
  documentId: string
  /** The selected passage, or an app view's own words for what it named. */
  label: string
  /**
   * An opaque app-scoped handle: a canvas node id, a row key.
   *
   * The host never resolves it and never validates it, because it names
   * something INSIDE a document rather than a document. It reaches the agent as
   * text and grants nothing on its own.
   */
  ref?: string
}

export interface SelectionState {
  selection: Selection | null
  /** Report one, or null to leave the current one alone (see STICKY above). */
  select: (next: Selection | null) => void
  /** Drop it deliberately. The only thing that clears a selection. */
  clear: () => void
}

const NOTHING: SelectionState = {
  selection: null,
  select: () => {},
  clear: () => {},
}

export const SelectionContext = createContext<SelectionState>(NOTHING)

/** The selection this window is holding. `NOTHING` outside a provider, so a
 *  pane rendered in a test or a slot with no shell simply has none. */
export const useSelection = (): SelectionState => useContext(SelectionContext)

/** Collapse whitespace, trim, bound. A label is one line in a row. */
export function labelFrom(raw: string): string {
  const text = raw.replace(/\s+/g, ' ').trim()
  return text.length > SELECTION_LABEL_MAX
    ? `${text.slice(0, SELECTION_LABEL_MAX - 1)}…`
    : text
}
